import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Typography,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Alert,
    Box,
    Button
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getCbIgnitionFuel, postCbIgnitionFuel } from '../../../../../actions/conservation-burns/conservation-burn-actions';
import useTsPaginator from 'ts-paginator';
import ClearIcon from '@mui/icons-material/Clear';
import Paginator from '../../../../layout/Pagination';
import EmptySelect from '../../shared-components/project-info/general-info/shared-components/EmptySelect';


const CB_FUEL_HEADERS = [
    'Date',
    'Fuel Type (ff)',
    'Refill Volume (gal / L)',
    'Source Document',
    'Remove Row'
]

const CB_IGNITION_FUEL_TYPES = [
    'Diesel',
    'Petrol',
    'Propane',
    'LPG'
]

/**
 * Normalizes width styling for the table header
 * @param {string} header 
 * @returns {string} Portion of the table width as string percent _i.e. '20%'_
 */

const CB_FUEL_COL_WIDTH = (header) => {
    const remove_row_width = 10
    const full_width = 100
    const data_columns = CB_FUEL_HEADERS.length - 1
    const leftover_width = full_width - remove_row_width
    if (header === 'Remove Row') {
        return `${remove_row_width}%`
    }
    else return `${leftover_width / data_columns}%`;
}

export default function CbFuel({ fuelData, projectId }) {
    const [ignitionFuelState, setIgnitionFuelState] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const dispatch = useDispatch()
    const { userData } = useSelector(state => state.auth);
    const tableRecordCount = useSelector((state) => (state.conservationBurnsReducer.ignitionFuel?.pagination?.total_records))
    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    useEffect(() => {
        dispatch(getCbIgnitionFuel(userData.id, projectId, rowsPerPage, currentPage))
        setIgnitionFuelState([]);
    }, [projectId, dispatch, rowsPerPage, currentPage]);

    useEffect(() => {
        _handleChangePage(0); // Reset to the first page
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount); // Reset to the first page
    }, [projectId, tableRecordCount]);

    useEffect(() => {
        const stateArray = fuelData?.map((item) => ({
            id: item.id,
            rowId: uuidv4(),
            date: item.date,
            fuelType: item.fuelType,
            refillVolume: item.refillVolume,
            sourceDocument:
            {
                fileUrl: item.sourceDocument?.fileUrl,
                fileName: item.sourceDocument?.fileName,
            },
            dirty: false,
        })) || [];
        setIgnitionFuelState(stateArray);
    }, [fuelData]);

    const handleAddItem = () => {
        setIgnitionFuelState((prevState) => {
            const state = [...prevState]
            const newStateObject = {
                id: null,
                rowId: uuidv4(),
                date: '',
                fuelType: '',
                refillVolume: null,
                sourceDocument: null,
                dirty: false,
            }
            state.push(newStateObject)
            return state
        })
    };

    const handleInputChange = (e, dataId, field) => {
        setIgnitionFuelState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    item[field] = e.target.value;
                    item.dirty = true;
                }
                return item;
            })
        );
    };

    const handleDateSelection = (date, dataId) => {
        setIgnitionFuelState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    item.date = moment(date).format('YYYYMMDD');
                    item.dirty = true;
                }
                return item;
            })
        );
    };

    const handleFileUpload = (e, dataId, field) => {
        const file = e.target.files[0];
        setIgnitionFuelState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    item[field] = file;
                    item.dirty = true;
                }
                return item;
            })
        );
    };

    const handleSubmitData = async () => {
        try {
            setLoading(true);
            let hasError = false;
            ignitionFuelState.forEach((stateItem) => {
                if (!stateItem.date || !stateItem.fuelType || !stateItem.refillVolume || !stateItem.sourceDocument) {
                    setError(true);
                    hasError = true;
                    return;
                }
            });
            if (!hasError) {
                const formData = new FormData();
                formData.append("data", JSON.stringify(ignitionFuelState));

                ignitionFuelState.forEach((item, index) => {
                    if (item.sourceDocument instanceof File) {
                        formData.append(`sourceDocument_${index}`, item.sourceDocument);
                    }
                });

                await dispatch(postCbIgnitionFuel(userData.id, projectId, rowsPerPage, currentPage, formData))
                setError(false);
            }
        } catch (error) {
            return
        } finally { setLoading(false) }
    };

    const handleClearEntry = (rowIdToRemove) => {
        setIgnitionFuelState((prevState) =>
            prevState.filter((item) => item.rowId !== rowIdToRemove)
        );
    }

    return (
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">
                Ignition Fuel
            </Typography>
            <TableContainer component={Paper} sx={{ height: 'auto' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {CB_FUEL_HEADERS.map((header) => (
                                <TableCell
                                    key={header}
                                    align='left'
                                    sx={{
                                        minWidth: CB_FUEL_COL_WIDTH(header),
                                        width: CB_FUEL_COL_WIDTH(header)
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ignitionFuelState?.map((stateItem, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableCell
                                        align="left"
                                    >
                                        <FormControl>
                                            <LocalizationProvider
                                                dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    slotProps={{
                                                        textField: {
                                                            error: false,
                                                        }
                                                    }}
                                                    name={'date'}
                                                    value={stateItem.date
                                                        ? moment(stateItem.date, 'YYYYMMDD')
                                                        : null}
                                                    onChange={(date) => handleDateSelection(date, stateItem.rowId)}
                                                    disabled={stateItem.disabled}
                                                    format="MM/DD/YYYY"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell
                                        align="left"
                                    >
                                        <Select
                                            name='fuelType'
                                            value={stateItem.fuelType || ''}
                                            disabled={stateItem.disabled}
                                            displayEmpty
                                            sx={{ maxWidth: '50%', width: 300 }}
                                            renderValue={(selected) => (selected ? selected : EmptySelect({ text: 'Type' }))}
                                            onChange={(e) => handleInputChange(e, stateItem.rowId, 'fuelType')}>
                                            {CB_IGNITION_FUEL_TYPES?.map((fuelType) => (
                                                <MenuItem
                                                    key={fuelType}
                                                    value={fuelType}
                                                >
                                                    {fuelType}
                                                </MenuItem>
                                            )
                                            )}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name='refillVolume'
                                            value={stateItem.refillVolume || ''}
                                            onChange={(e) => handleInputChange(e, stateItem.rowId, 'refillVolume')}
                                            placeholder='Refill Amount'
                                            inputProps={{
                                                sx: {
                                                    "&::placeholder": {
                                                        fontStyle: 'italic'
                                                    }
                                                }
                                            }}
                                            type="text"
                                            disabled={stateItem.disabled}

                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        {stateItem.sourceDocument
                                            ? (
                                                <a
                                                    href={
                                                        stateItem.sourceDocument instanceof File
                                                            ? URL.createObjectURL(stateItem.sourceDocument)
                                                            : stateItem.sourceDocument?.fileUrl ?
                                                                stateItem.sourceDocument?.fileUrl
                                                                : ''
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    Document
                                                </a>
                                            )
                                            : (
                                                <TextField
                                                    name={'sourceDocument'}
                                                    defaultValue={stateItem.sourceDocument?.fileName
                                                        ? stateItem.sourceDocument?.fileName
                                                        : ''}
                                                    onChange={(e) => handleFileUpload(e, stateItem.rowId, 'sourceDocument')}
                                                    type="file"
                                                />
                                            )}
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        {stateItem.rowId && !stateItem.id && index === ignitionFuelState.length - 1 &&
                                            <Box onClick={() => handleClearEntry(stateItem.rowId)}
                                                sx={{ cursor: 'pointer', color: 'red', mt: 1 }}>
                                                <ClearIcon />
                                            </Box>
                                        }
                                    </TableCell>
                                </TableRow>
                            )
                        }

                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Paginator
                totalRecordCount={totalRecordCount}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                handleChangePage={_handleChangePage}
                handleChangeRowsPerPage={_handleChangeRowsPerPage}
                determinePaginationMessage={_determinePaginationMessage}
                determinePaginationDisabledState={_determinePaginationDisabledState}
                determineRowsPerPageOptions={_determineRowsPerPageOptions}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {error &&
                    <Alert severity='error' sx={{ width: 'fit-content' }}>
                        Please fill out required fields before submitting
                    </Alert>
                }
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'flex-start' }}>
                    <Button
                        variant='contained'
                        sx={{ width: 'fit-content' }}
                        onClick={handleAddItem}
                    >
                        Add Entry
                    </Button>
                    <Button
                        variant='contained'
                        sx={{ width: 'fit-content' }}
                        onClick={handleSubmitData}
                        disabled={loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Data'}
                    </Button>
                </Box>
            </Box>
        </Grid >
    )
}
